import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ToastPopup from '../Popups/ToastPopup';
import { apiCall } from '../main/Apicall';
import "../css/others/template.css";
import { CreateEmailTemplate, createToastMessage, ToastMessage } from "../Interfaces/Interface";
const CreateTemplate: React.FC = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const username = 'Admin';
  const extractPlaceholders = (...texts: string[]): string[] => {
    const placeholderPattern = /\{\{([^}]+)\}\}/g;
    const matches = texts.flatMap(text => {
      const foundMatches: string[] = [];
      let match;
      const textStr = text || '';
      const regex = new RegExp(placeholderPattern);
      while ((match = regex.exec(textStr)) !== null) {
        foundMatches.push(match[0]);
      }
      return foundMatches;
    });
    return [...new Set(matches)];
  };
  const escapeNewLines = (text: string) => text.replace(/\n/g, '\\n');
  const unescapeNewLines = (text: string) => text.replace(/\\n/g, '\n');
  const initialTemplate: CreateEmailTemplate = {
    name: '',
    subject: 'Welcome to {{company_name}}!',
    body: `Dear {{your_first_name}},\n\nWelcome to {{your_company_name}}! We're excited to have you on board.\n\nBest regards,\n{{your_company_name}} Team`,
    clientId: clientId || '',
    type: '',
    placeholders: [],
    extraAttributes: {
      logoUrl: '',
      themeColor: '#1a73e8',
    }
  };
  const [template, setTemplate] = useState<CreateEmailTemplate>(initialTemplate);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionPosition, setSuggestionPosition] = useState({ top: 0, left: 0 });
  const [activeField, setActiveField] = useState<'subject' | 'body' | null>(null);
  const [availableTemplateTypes, setAvailableTemplateTypes] = useState<{ id: string; type: string }[]>([]);
  const [isLoadingTemplateTypes, setIsLoadingTemplateTypes] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loadedTemplateType, setLoadedTemplateType] = useState<string>('');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const newPlaceholders = extractPlaceholders(template.subject, template.body);
    setPlaceholders(newPlaceholders);
    setTemplate(prev => ({ ...prev, placeholders: newPlaceholders }));
  }, [template.subject, template.body]);
  useEffect(() => {
    const fetchTemplateTypes = async () => {
      if (clientId) {
        setIsLoadingTemplateTypes(true);
        try {
          const types = await apiCall('get', `/api/email-templates/default/types/${clientId}`);
          setAvailableTemplateTypes(types);
        } catch (error: any) {
          setMessage(createToastMessage('Failed to load template types', "error", 3000));
          setAvailableTemplateTypes([]);
        } finally {
          setIsLoadingTemplateTypes(false);
        }
      }
    };
    fetchTemplateTypes();
  }, [clientId]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, selectionStart } = e.target;
    if (name === 'type') {
      setTemplate(prev => ({
        ...prev,
        [name]: value.toUpperCase().replace(/\s+/g, '_')
      }));
    } else {
      setTemplate(prev => ({ ...prev, [name]: value }));
    }
    if (selectionStart !== null && value.slice(0, selectionStart).includes('{{')) {
      const rect = e.target.getBoundingClientRect();
      setSuggestionPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
      setActiveField(name as 'subject' | 'body');
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };
  const handleTemplateTypeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setSelectedTemplateId(selectedId);
    if (selectedId) {
      handleTemplateSelect(selectedId);
    } else {
      setTemplate(prev => ({
        ...prev,
        name: '',
        subject: initialTemplate.subject,
        body: initialTemplate.body,
        type: ''
      }));
      setLoadedTemplateType('');
    }
  };
  const insertPlaceholder = (placeholder: string) => {
    if (!activeField) return;
    const field = activeField === 'subject' ? subjectRef.current : bodyRef.current;
    if (!field) return;
    const start = field.selectionStart ?? 0;
    const end = field.selectionEnd ?? 0;
    const value = field.value;
    const newValue = value.slice(0, start) + placeholder + value.slice(end);
    setTemplate(prev => ({ ...prev, [activeField]: newValue }));
    setTimeout(() => {
      const cursorPos = start + placeholder.length;
      field.focus();
      field.setSelectionRange(cursorPos, cursorPos);
    }, 0);
    setShowSuggestions(false);
  };
  const validateForm = (): boolean => {
    if (!template.name.trim()) {
      setMessage(createToastMessage('Please enter a template name', "warning", 3000));
      return false;
    }
    if (!template.type.trim()) {
      setMessage(createToastMessage('Please enter a template type', "warning", 3000));
      return false;
    }
    if (!template.subject.trim()) {
      setMessage(createToastMessage('Please enter a subject', "warning", 3000));
      return false;
    }
    if (!template.body.trim()) {
      setMessage(createToastMessage('Please enter a body', "warning", 3000));
      return false;
    }
    if (loadedTemplateType && template.type === loadedTemplateType) {
      setMessage(createToastMessage('Template type cannot be the same as the loaded template', "warning", 3000));
      return false;
    }
    return true;
  };
  const handleTemplateSelect = async (templateId: string) => {
    if (!templateId || !clientId) return;
    try {
      setIsLoading(true);
      const data = await apiCall('get', `/api/email-templates/client/${clientId}/template/${templateId}`);
      const bodyWithNewLines = unescapeNewLines(data.body || '');
      setTemplate({ name: data.name || '', subject: data.subject || '', body: bodyWithNewLines, clientId: data.clientId || clientId, type: '', placeholders: data.placeholders || [], extraAttributes: data.extraAttributes || { logoUrl: '', themeColor: '#1a73e8' } });
      setLoadedTemplateType(data.type || '');
    } catch (error: any) {
      try {
        const defaultData = await apiCall('get', `/api/email-templates/${templateId}`);
        const bodyWithNewLines = unescapeNewLines(defaultData.body || '');
        setTemplate({ name: defaultData.name || '', subject: defaultData.subject || '', body: bodyWithNewLines, clientId: defaultData.clientId || clientId, type: '', placeholders: defaultData.placeholders || [], extraAttributes: defaultData.extraAttributes || { logoUrl: '', themeColor: '#1a73e8' } });
        setLoadedTemplateType(defaultData.type || '');
      } catch (fallbackError: any) {
        setMessage(createToastMessage('Failed to load template details. Please check if the template exists.', "error", 3000));
        setTemplate(prev => ({ ...prev, name: '', subject: initialTemplate.subject, body: initialTemplate.body, type: '' }));
        setLoadedTemplateType('');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setIsLoading(true);
      const finalTemplate = { ...template, body: escapeNewLines(template.body), placeholders: extractPlaceholders(template.subject, template.body) };
      await apiCall('post', `/api/email-templates/client/${clientId}?username=${encodeURIComponent(username)}`, finalTemplate);
      setMessage(createToastMessage('Template created successfully!', "success", 3000));
      setTimeout(() => navigate(-1), 2000);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || "Something went wrong while creating the template.";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    } finally {
      setIsLoading(false);
    }
  };
  const handleBack = () => navigate(-1);
  const SuggestionBox = () => (
    <div className="suggestion-box" style={{ top: suggestionPosition.top, left: suggestionPosition.left }}>
      <ul className="suggestion-list">
        {placeholders.map((ph, idx) => (
          <li key={idx} onClick={() => insertPlaceholder(ph)}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}> {ph} </li>
        ))}
      </ul>
    </div>
  );
  const displayBody = template.body;
  return (
    <div className="template-page-container">
      <div className="template-page-header">
        <h2>Create New Email Template</h2>
      </div>
      <div className="template-page-form">
        <div className="template-page-header">
          <h2>Select from Sample Templates</h2>
        </div>
        {isLoadingTemplateTypes ? (
          <select disabled>
            <option>Loading sample templates...</option>
          </select>
        ) : (
          <select id="sampleTemplates" value={selectedTemplateId} onChange={handleTemplateTypeSelect} disabled={isLoading}>
            <option value="">Select a sample template</option>
            {availableTemplateTypes.map((t, index) => (
              <option key={t.id} value={t.id}>{t.type} </option>
            ))}
          </select>
        )}
        <small className="small">Choose from pre-defined sample templates to load their content into the form below</small>
      </div>
      <form id="templateForm" className="template-page-form" onSubmit={handleSubmit}>
        <div className="template-page-field">
          <label htmlFor="templateName">Template Name</label>
          <input id="templateName" name="name" value={template.name} onChange={handleChange} placeholder="e.g., Enter an Email Template Name" disabled={isLoading} />
        </div>
        <div className="template-page-field">
          <label htmlFor="templateType">Template Type</label>
          <input id="templateType" name="type" value={template.type} onChange={handleChange} placeholder="e.g., USER_WELCOME, PASSWORD_RESET, etc." disabled={isLoading} required />
          <small className="small"> Will be automatically converted to UPPER_CASE_WITH_UNDERSCORES
            {loadedTemplateType && (
              <span className="small-span"> Must be different from loaded template type: {loadedTemplateType} </span>
            )}
          </small>
        </div>
        <div className="template-page-field">
          <label htmlFor="templateSubject">Subject</label>
          <input id="templateSubject" ref={subjectRef} name="subject" value={template.subject} onChange={handleChange} placeholder="e.g., Welcome to {{company_name}}!" disabled={isLoading} />
        </div>
        <div className="template-page-field">
          <label htmlFor="templateBody">Body</label>
          <textarea id="templateBody" ref={bodyRef} name="body" rows={10} value={displayBody} onChange={handleChange} placeholder={`Dear {{first_name}},\n\nWelcome to {{company_name}}! We're excited to have you on board.\n\nBest regards,\n{{company_name}} Team`} disabled={isLoading} className="template-body" />
          {placeholders.length > 0 && (
            <div className="template-page-placeholders">
              <strong>Detected Placeholders:</strong> {placeholders.join(', ')}
            </div>
          )}
        </div>
      </form>
      <div className="buttons2">
        <button type="button" onClick={handleBack} className="cancelbutton2" disabled={isLoading}> Back</button>
        <button type="submit" form="templateForm" disabled={isLoading} className="savebutton2" > {isLoading ? "Creating..." : "Create Template"}</button>
      </div>
      {showSuggestions && <SuggestionBox />}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default CreateTemplate;